import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'Who can submit?',
    answer:
      'Anyone, anywhere in the world. All nationalities, all languages — English subtitles required for non-English dialogue. First-time filmmakers, veterans, students, professionals. If you made it, we want to see it.',
  },
  {
    question: 'Does my film need to be a premiere?',
    answer:
      'World, international, and online premiere status is preferred but not required. Films currently on Vimeo, YouTube, or other platforms are fully eligible. We care about the work, not the premiere status.',
  },
  {
    question: 'What is the Scene category?',
    answer:
      "A scene submission is a single scene — any genre, any length. It could be a scene from a larger work in progress, a standalone piece, or a proof of concept. It's the lowest-barrier entry point in the festival and is judged purely on the quality of the work within that scene.",
  },
  {
    question: 'What is a Vertical Film?',
    answer:
      'Vertical films are shot specifically for portrait orientation — the format native to phones and platforms like TikTok and Instagram. We believe vertical is a legitimate cinematic format and it deserves its own category and its own prize.',
  },
];

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="cursor-pointer"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-6 text-left"
      >
        <span
          className="text-white font-medium"
          style={{ fontSize: '0.95rem' }}
        >
          {question}
        </span>
        <span
          className="text-teal flex-shrink-0 ml-4 transition-transform duration-300"
          style={{
            fontSize: '1.2rem',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          +
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p
              className="font-light pb-6"
              style={{
                fontSize: '0.88rem',
                color: 'rgba(245,245,240,0.55)',
                lineHeight: 1.8,
              }}
            >
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQPreview() {
  return (
    <section className="max-w-[1200px] mx-auto px-6 py-24">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        {/* Eyebrow */}
        <span
          className="font-mono text-teal uppercase block mb-4"
          style={{
            fontSize: '0.7rem',
            letterSpacing: '0.25em',
          }}
        >
          FAQ
        </span>

        {/* Title */}
        <h2
          className="font-display text-white leading-none mb-12"
          style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
        >
          Common questions.
        </h2>
      </motion.div>

      {/* FAQ Accordion */}
      <div className="max-w-[720px]">
        {faqs.map((faq, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
          >
            <FAQItem question={faq.question} answer={faq.answer} />
          </motion.div>
        ))}
      </div>

      {/* See All Link */}
      <motion.div
        className="mt-10"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.3 }}
      >
        <Link
          to="/faq"
          className="text-teal hover:underline transition-colors"
          style={{ fontSize: '0.9rem' }}
        >
          See all FAQs →
        </Link>
      </motion.div>
    </section>
  );
}
